import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Appearance, View, Text, Pressable } from 'react-native';
import { storeCache, getCache } from '../utils/cacheStore';
import { setActiveTheme, ThemeMode } from './styles';
import { lightTheme, lightStyleTheme } from './lightTheme';
import { darkTheme, darkStyleTheme } from './darkTheme';

export type ThemePreference = 'system' | 'light' | 'dark';

const CACHE_KEY = 'themePreference';

interface ThemeContextValue {
    mode: ThemeMode;
    preference: ThemePreference;
    setPreference: (p: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
    mode: 'light',
    preference: 'system',
    setPreference: () => {},
});

function resolveSystem(): ThemeMode {
    return Appearance.getColorScheme() === 'dark' ? 'dark' : 'light';
}

function resolveMode(pref: ThemePreference, system: ThemeMode): ThemeMode {
    return pref === 'system' ? system : pref;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [preference, setPreferenceState] = useState<ThemePreference>('system');
    const [systemMode, setSystemMode] = useState<ThemeMode>(resolveSystem());
    const [hydrated, setHydrated] = useState(false);

    useEffect(() => {
        getCache(CACHE_KEY)
            .then((value: any) => {
                if (value === 'light' || value === 'dark' || value === 'system') {
                    setPreferenceState(value);
                }
            })
            .catch(() => { /* ignore */ })
            .finally(() => setHydrated(true));
    }, []);

    useEffect(() => {
        const sub = Appearance.addChangeListener(({ colorScheme }) => {
            setSystemMode(colorScheme === 'dark' ? 'dark' : 'light');
        });
        return () => sub.remove?.();
    }, []);

    const mode = resolveMode(preference, systemMode);
    setActiveTheme(mode);

    const setPreference = useCallback((p: ThemePreference) => {
        storeCache(CACHE_KEY, p);
        setPreferenceState(p);
    }, []);

    if (!hydrated) return null;

    return (
        <ThemeContext.Provider value={{ mode, preference, setPreference }} key={mode}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    return useContext(ThemeContext);
}

const OPTIONS: { key: ThemePreference; label: string }[] = [
    { key: 'system', label: 'System' },
    { key: 'light', label: 'Light' },
    { key: 'dark', label: 'Dark' },
];

export function ThemeToggle() {
    const { mode, preference, setPreference } = useTheme();
    const t = mode === 'dark' ? darkTheme : lightTheme;
    const st = mode === 'dark' ? darkStyleTheme : lightStyleTheme;
    const isDark = mode === 'dark';

    const trackBg = isDark ? '#14110C' : '#F2EFE9';
    const trackBorder = isDark ? '#2A2620' : '#E4E0D6';
    const activeBg = isDark ? '#2A241C' : '#FFFFFF';
    const activeBorder = isDark ? '#3A352D' : '#D9D2C2';
    const labelColor = isDark ? '#F2ECDC' : '#1A1612';
    const mutedColor = isDark ? '#A89F8B' : '#4A463E';
    const accent = st.primaryButtonColor;

    return (
        <View style={{ width: '100%', marginTop: 12, marginBottom: 4 }}>
            <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 8,
                paddingHorizontal: 2,
            }}>
                <Text style={{
                    fontSize: 13,
                    fontWeight: '600',
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    color: mutedColor,
                }}>Appearance</Text>
                <Text style={{
                    fontSize: 11,
                    color: mutedColor,
                    fontStyle: 'italic',
                }}>{preference === 'system' ? `Following system · ${mode}` : preference}</Text>
            </View>
            <View style={{
                flexDirection: 'row',
                backgroundColor: trackBg,
                borderRadius: 10,
                borderWidth: 1,
                borderColor: trackBorder,
                padding: 3,
            }}>
                {OPTIONS.map(opt => {
                    const active = preference === opt.key;
                    return (
                        <Pressable
                            key={opt.key}
                            onPress={() => setPreference(opt.key)}
                            style={{
                                flex: 1,
                                paddingVertical: 8,
                                borderRadius: 7,
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: active ? activeBg : 'transparent',
                                borderWidth: active ? 1 : 0,
                                borderColor: active ? activeBorder : 'transparent',
                            }}>
                            <Text style={{
                                fontSize: 13,
                                fontWeight: active ? '600' : '500',
                                color: active ? accent : labelColor,
                                letterSpacing: 0.2,
                            }}>{opt.label}</Text>
                        </Pressable>
                    );
                })}
            </View>
        </View>
    );
}
